import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { updateRecognition, uploadFiles } from 'api/recognitions.service';
import {
  generateRecognitionRequestPayload,
  useAppLocation,
} from 'hooks/helpers';
import { useToast } from 'hooks/ui';
import { Recognition } from 'models/recognition.model';
import { recognitionRoutes } from 'router/recognitionRoutes';
import { useApplicationStore } from 'store/application';
import { setDraftRecognition } from 'store/recognition';
import { recognitionKeys } from './queryKeys';

export const useRecognitionUpdateQuery = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setLoading, removeLoading } = useApplicationStore((state) => state);
  const { isCreate } = useAppLocation(recognitionRoutes);

  const { isPending, mutate } = useMutation({
    mutationFn: async (recognition: Recognition) => {
      const newRecognition = generateRecognitionRequestPayload(recognition);

      setLoading();
      if (recognition.reward === 'PICTURE') {
        return updateRecognition(recognition?.id, {
          ...newRecognition,
          attachments: recognition.attachments.filter((f) => f.id),
        }).then(async (res) => {
          const attachments = recognition.attachments?.filter(
            (attachment) => !attachment.id,
          );

          if (attachments?.length) {
            await uploadFiles(res.id, attachments);
          }

          return res;
        });
      }

      return updateRecognition(recognition?.id, newRecognition);
    },
    onSuccess: (recognition) => {
      toast.success('Recognition has been drafted successfully');
      if (!isCreate) setDraftRecognition(recognition);
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: recognitionKeys.all });
      removeLoading();
    },
  });

  return { isLoading: isPending, mutate };
};
